/*
 * Renders a shareable result-card image (PNG) on a canvas — almanac-styled, so
 * it matches the app and the OG banner. Used for image sharing / download.
 */

const SANS =
  "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
const SERIF = "'Fraunces', Georgia, 'Times New Roman', serif"

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

export interface ResultCardOpts {
  categoryName: string
  puzzleNumber: number
  checks: boolean[][]
  won: boolean
  maxAttempts: number
  streak: number
}

export async function renderResultCard(opts: ResultCardOpts): Promise<Blob | null> {
  const { categoryName, puzzleNumber, checks, won, maxAttempts, streak } = opts
  const W = 1080
  const H = 1080
  const PAD = 96

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  try {
    await Promise.all([
      (document as Document & { fonts: FontFaceSet }).fonts.load('600 80px Fraunces'),
      (document as Document & { fonts: FontFaceSet }).fonts.load('500 40px Fraunces'),
    ])
  } catch {
    /* fall back to serif stack */
  }

  // background
  ctx.fillStyle = '#15120c'
  ctx.fillRect(0, 0, W, H)

  ctx.textBaseline = 'top'
  ctx.textAlign = 'left'

  // brand eyebrow (manual letter-spacing)
  ctx.fillStyle = '#e2a63c'
  ctx.font = `700 26px ${SANS}`
  ctx.fillText('L I N E U P', PAD, PAD)

  // category + puzzle number
  ctx.fillStyle = '#f2ecdd'
  ctx.font = `600 76px ${SERIF}`
  ctx.fillText(categoryName, PAD, PAD + 40)
  const catW = ctx.measureText(categoryName).width
  ctx.fillStyle = '#9a917c'
  ctx.font = `500 38px ${SERIF}`
  ctx.fillText(`№ ${puzzleNumber}`, PAD + catW + 22, PAD + 78)

  // grid of checks
  const cols = 6
  const sq = 116
  const gap = 16
  const gridW = cols * sq + (cols - 1) * gap
  const gx = (W - gridW) / 2
  const rows = Math.max(checks.length, 1)
  const gridH = rows * sq + (rows - 1) * gap
  const gy = (H - gridH) / 2 - 30

  checks.forEach((row, r) => {
    row.forEach((ok, c) => {
      const x = gx + c * (sq + gap)
      const y = gy + r * (sq + gap)
      roundRect(ctx, x, y, sq, sq, 18)
      if (ok) {
        ctx.fillStyle = '#6cc081'
        ctx.fill()
      } else {
        ctx.fillStyle = '#221c12'
        ctx.fill()
        ctx.strokeStyle = '#4a4230'
        ctx.lineWidth = 2
        ctx.stroke()
      }
    })
  })

  // score + streak
  ctx.textAlign = 'center'
  const scoreText = won ? `${checks.length}/${maxAttempts}` : `X/${maxAttempts}`
  ctx.fillStyle = '#f2ecdd'
  ctx.font = `600 92px ${SERIF}`
  ctx.fillText(scoreText, W / 2, gy + gridH + 56)

  if (streak > 0) {
    ctx.fillStyle = '#e2a63c'
    ctx.font = `600 42px ${SANS}`
    ctx.fillText(`🔥 ${streak}`, W / 2, gy + gridH + 176)
  }

  // footer
  ctx.fillStyle = '#9a917c'
  ctx.font = `500 30px ${SANS}`
  ctx.fillText('jrkb6.dev/lineup', W / 2, H - PAD - 6)

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'))
}

/** Shares the result image via the Web Share API, or downloads it as a fallback. */
export async function shareOrDownloadCard(
  opts: ResultCardOpts,
  text: string,
): Promise<'shared' | 'downloaded' | 'failed'> {
  const blob = await renderResultCard(opts)
  if (!blob) return 'failed'
  const file = new File([blob], 'lineup.png', { type: 'image/png' })

  const nav = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean
  }
  try {
    if (nav.canShare && nav.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text })
      return 'shared'
    }
  } catch {
    return 'failed'
  }

  // Fallback: trigger a download.
  try {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'lineup.png'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
    return 'downloaded'
  } catch {
    return 'failed'
  }
}
